import React, { useEffect, useRef, useState } from 'react';
import { VoicePlayer } from '../VoiceNote';
import { fileSize } from '../ChatFile';
import { Icon } from './icons';

/*
 * A note's photos and voice notes (batch 5).
 *
 * Like every chat file (ChatFile.js), the bytes are FETCHED with the sign-in
 * - /todos/attachments/<id> wants a token in a header, and an <img src>
 * cannot carry one - and shown from an object URL. The same photo is on the
 * card in the grid and again in the editor, so the object URLs are shared
 * here, counted, and let go a little after the last one showing it has
 * gone: a grid of forty photo notes must not hold forty downloads forever,
 * nor fetch each one twice.
 */

const cache = new Map();   // attachment id -> { promise, url, refs, timer }

function acquire(att, load) {
  let e = cache.get(att.id);
  if (!e) {
    e = { url: null, refs: 0, timer: null, promise: null };
    e.promise = Promise.resolve(load(att)).then((blob) => {
      e.url = URL.createObjectURL(blob);
      return e.url;
    });
    e.promise.catch(() => cache.delete(att.id));
    cache.set(att.id, e);
  }
  if (e.timer) { clearTimeout(e.timer); e.timer = null; }
  e.refs += 1;
  return e;
}

function release(id) {
  const e = cache.get(id);
  if (!e) return;
  e.refs -= 1;
  if (e.refs > 0) return;
  e.timer = setTimeout(() => {
    if (e.refs > 0) return;
    cache.delete(id);
    if (e.url) URL.revokeObjectURL(e.url);
  }, 30000);
}

/** The object URL of one attachment, or null while it is on its way (or failed). */
export function useAttachmentUrl(att, load) {
  const [url, setUrl] = useState(() => cache.get(att?.id)?.url || null);
  const loadRef = useRef(load);
  loadRef.current = load;
  const id = att?.id;
  useEffect(() => {
    if (!id || !loadRef.current) return undefined;
    let live = true;
    const e = acquire(att, loadRef.current);
    e.promise.then((u) => { if (live) setUrl(u); }).catch(() => { if (live) setUrl(null); });
    return () => { live = false; release(id); };
    // The attachment is named by its id; a new object for the same id is the same file.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  return url;
}

/**
 * One photo. A failed fetch draws a quiet placeholder with the file's name
 * rather than a broken-image icon, which reads as a fault when the note is
 * fine.
 */
export function NoteImage({ att, load, className = '' }) {
  const url = useAttachmentUrl(att, load);
  return url ? (
    <img className={`tdn-img ${className}`} src={url} alt={att.file_name || 'Photo'} draggable={false}
         data-attachment={att.id} />
  ) : (
    <span className={`tdn-img is-waiting ${className}`} data-attachment={att.id} role="img"
          aria-label={att.file_name || 'Photo'} />
  );
}

/** A photo opened large, over everything; Escape or a press anywhere closes it. */
export function ImageViewer({ att, load, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return (
    <div className="tdn-viewer" role="dialog" aria-modal="true" aria-label={att.file_name || 'Photo'}
         onClick={onClose}>
      <NoteImage att={att} load={load} className="is-large" />
      <span className="tdn-viewer-name">{att.file_name}{att.size_bytes ? ` · ${fileSize(att.size_bytes)}` : ''}</span>
      <button type="button" className="tdn-viewer-x" aria-label="Close photo" onClick={onClose}><Icon name="close" size={20} /></button>
    </div>
  );
}

/** A voice note on a note: chat's player, fed from the to-do routes. */
export function NoteVoice({ att, load, download, onError, onRemove = null }) {
  return (
    <span className="tdn-voice" data-voice={att.id}>
      <VoicePlayer file={{ ...att, file_name: att.file_name || 'Voice note' }} onError={onError}
                   load={load} download={download} durationMs={att.duration_ms} />
      {onRemove && (
        <button type="button" className="tdn-remove" aria-label={`Remove ${att.file_name || 'voice note'}`}
                title="Remove" onClick={onRemove}><Icon name="close" size={14} /></button>
      )}
    </span>
  );
}
